import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { logger } from '@/utils/logger';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  RefreshCw,
  Download,
  Plus,
  X,
} from 'lucide-react';
import { clsx } from 'clsx';
import { Booking } from '@/types';
import BookingCard from '@/components/common/BookingCard';
import { api } from '@/services/api';
import toast from 'react-hot-toast';
import Modal from '@/components/ui/Modal';
import { formatNZD } from '@/utils/currency';
import { getBookingEffectiveStatus } from '@/utils/bookingStatus';
import { getNzDateString } from '@/utils/nzDate';

const escapeCsvValue = (value: unknown) => {
  const text = value === null || value === undefined ? '' : String(value);
  const needsQuotes = /[",\n\r]/.test(text);
  const escaped = text.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
};

const downloadCsv = (filename: string, csvContent: string) => {
  const blob = new Blob([`\ufeff${csvContent}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const BookingList: React.FC = () => {
  const { t } = useTranslation();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(() => searchParams.get('date') || getNzDateString());
  const [allDates, setAllDates] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [realTimeUpdate, setRealTimeUpdate] = useState(true);
  const [cancelTarget, setCancelTarget] = useState<Booking | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Booking | null>(null);
  const [newBookingIds, setNewBookingIds] = useState<Set<number>>(new Set());
  const knownBookingIdsRef = useRef<Set<number>>(new Set());
  const isFirstLoadRef = useRef(true);
  const navigate = useNavigate();

  // 防抖搜索词
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // 筛选条件变化时重置新订单追踪（避免切换日期/搜索后误标）
  useEffect(() => {
    knownBookingIdsRef.current = new Set();
    isFirstLoadRef.current = true;
    setNewBookingIds(new Set());
  }, [selectedDate, allDates, debouncedSearch]);

  const fetchBookings = useCallback(async (showToast = false) => {
    setLoading(true);
    const params: Record<string, unknown> = { limit: 100 };
    if (!allDates) params.date = selectedDate;
    if (debouncedSearch.length >= 2) params.search = debouncedSearch;

    const result = await api.bookings.getAll(params as Parameters<typeof api.bookings.getAll>[0]);
    if (result.error || !result.data) {
      const message = t('errors.serverError');
      logger.error('Failed to fetch bookings:', result.error);
      setError(message);
      setBookings([]);
      if (showToast) {
        toast.error(message);
      }
    } else {
      const normalized = (result.data.bookings || []).map((booking) => ({
        ...booking,
        status: getBookingEffectiveStatus(booking),
      }));

      // 新订单检测
      const currentIds = new Set(normalized.map((b) => b.id));
      if (isFirstLoadRef.current) {
        // 首次加载：所有 ID 直接进入 known set，不触发动画
        knownBookingIdsRef.current = currentIds;
        isFirstLoadRef.current = false;
      } else {
        const freshIds: number[] = [];
        currentIds.forEach((id) => {
          if (!knownBookingIdsRef.current.has(id)) {
            freshIds.push(id);
          }
        });
        if (freshIds.length > 0) {
          // 更新 known set
          freshIds.forEach((id) => knownBookingIdsRef.current.add(id));
          // 合并到 newBookingIds state
          setNewBookingIds((prev) => {
            const next = new Set(prev);
            freshIds.forEach((id) => next.add(id));
            return next;
          });
          // toast 提示
          toast.success(
            `${freshIds.length} ${t('bookings.newBookingsArrived', { count: freshIds.length })}`,
            { icon: '🔔', duration: 4000 }
          );
          // 5 分钟后自动移除
          setTimeout(() => {
            setNewBookingIds((prev) => {
              const next = new Set(prev);
              freshIds.forEach((id) => next.delete(id));
              return next;
            });
          }, 5 * 60 * 1000);
        }
      }

      setBookings(normalized);
      setError(null);
      if (showToast) {
        toast.success(t('common.dataRefreshed'));
      }
    }
    setLoading(false);
  }, [allDates, selectedDate, debouncedSearch, t]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    if (!realTimeUpdate) return;
    const intervalId = setInterval(() => {
      fetchBookings(false);
    }, 15000);
    return () => clearInterval(intervalId);
  }, [realTimeUpdate, fetchBookings]);

  // 刷新数据函数
  const refreshBookings = () => {
    fetchBookings(true);
  };

  // 筛选数据（搜索由后端处理，前端只做状态筛选）
  const filteredBookings = useMemo(() => {
    if (selectedStatus === 'all') return bookings;
    return bookings.filter(booking => booking.status === selectedStatus);
  }, [bookings, selectedStatus]);

  // 一键操作函数
  const handleQuickAction = async (booking: Booking, action: string) => {
    logger.info(`Executing ${action} operation:`, booking.id);

    switch (action) {
      case 'confirm':
        await handleConfirm(booking);
        break;
      case 'confirm_and_pay':
        await handleConfirmAndPay(booking);
        break;
      case 'cancel':
        openCancelModal(booking);
        break;
      case 'restore':
        await handleRestore(booking);
        break;
      case 'delete':
        openDeleteModal(booking);
        break;
      case 'print_receipt':
        await handlePrintReceipt(booking);
        break;
      case 'complete':
        await handleComplete(booking);
        break;
      case 'view':
        navigate(`/bookings/${booking.id}?from_date=${selectedDate}`);
        break;
      case 'edit':
        navigate(`/bookings/${booking.id}?mode=edit&from_date=${selectedDate}`);
        break;
    }
  };

  const handleExport = () => {
    if (!filteredBookings.length) {
      toast.error(t('common.noDataToExport'));
      return;
    }

    const headers = [
      'id',
      'status',
      'booking_type',
      'date',
      'start_time',
      'end_time',
      'location_name',
      'court_name',
      'customer_name',
      'customer_phone',
      'customer_email',
      'player_count',
      'total_price',
      'payment_method',
      'payment_status',
      'created_at',
      'updated_at',
      'cancellation_reason'
    ];

    const lines = [
      headers.join(','),
      ...filteredBookings.map((booking) =>
        headers
          .map((key) => escapeCsvValue((booking as unknown as Record<string, unknown>)[key]))
          .join(',')
      )
    ];

    const today = new Date().toISOString().slice(0, 10);
    const fileDate = allDates ? `all_${today}` : selectedDate || today;
    downloadCsv(`bookings_export_${fileDate}.csv`, lines.join('\n'));
    toast.success(t('common.exportSuccess', { count: filteredBookings.length }));
  };

  const handleConfirm = async (booking: Booking) => {
    try {
      const toastId = toast.loading(t('booking.confirming'));
      const result = await api.bookings.updateStatus(booking.id, {
        status: 'confirmed',
        notes: t('booking.staffConfirmedBooking')
      });
      toast.dismiss(toastId);
      if (result.error) {
        toast.error(t('common.operationFailed'));
        logger.error('Confirm booking failed:', result.error);
        return;
      }
      toast.success(t('booking.confirmed', { id: booking.id }));
      refreshBookings();
    } catch (error) {
      toast.dismiss();
      toast.error(t('common.operationFailed'));
      logger.error('Confirm booking failed:', error);
    }
  };

  const handleConfirmAndPay = async (booking: Booking) => {
    try {
      const toastId = toast.loading(t('booking.confirmingAndProcessingPayment'));

      // 1. 首先确认预订
      const confirmResult = await api.bookings.updateStatus(booking.id, {
        status: 'confirmed',
        notes: t('booking.staffConfirmedBooking')
      });
      if (confirmResult.error) {
        toast.dismiss(toastId);
        toast.error(t('common.operationFailed'));
        logger.error('Payment confirmation failed (confirm):', confirmResult.error);
        return;
      }

      // 2. 处理支付
      if (booking.payment_method === 'cash') {
        const paymentResult = await api.payments.processCashPayment(booking.id, {
          amount: booking.total_price,
          notes: t('payment.onSitePayment')
        });
        if (paymentResult.error) {
          toast.dismiss(toastId);
          toast.error(t('common.operationFailed'));
          logger.error('Payment confirmation failed (cash):', paymentResult.error);
          return;
        }
      }

      toast.dismiss(toastId);
      toast.success(t('booking.confirmedAndPaid', { id: booking.id }));

      // 刷新数据
      refreshBookings();

    } catch (error) {
      toast.dismiss();
      toast.error(t('common.operationFailed'));
      logger.error('Payment confirmation failed:', error);
    }
  };

  const handleCancel = async (booking: Booking) => {
    try {
      const reason = cancelReason.trim();
      if (!reason) {
        setCancelError(t('booking.cancelReasonRequired'));
        return;
      }
      const toastId = toast.loading(t('booking.cancelling'));

      const result = await api.bookings.cancel(booking.id, reason);

      toast.dismiss(toastId);
      if (result.error) {
        if (result.status === 404) {
          toast.error(t('errors.resourceNotFound'));
          refreshBookings();
          closeCancelModal();
          return;
        }
        toast.error(t('booking.cancelFailed'));
        logger.error('Cancel booking failed:', result.error);
        return;
      }
      toast.success(t('booking.cancelled', { id: booking.id }));
      refreshBookings();
      closeCancelModal();

    } catch (error) {
      toast.dismiss();
      toast.error(t('booking.cancelFailed'));
      logger.error('Cancel booking failed:', error);
    }
  };

  const handleRestore = async (booking: Booking) => {
    try {
      const toastId = toast.loading(t('booking.restoring'));
      const result = await api.bookings.restore(booking.id);
      toast.dismiss(toastId);
      if (result.error) {
        if (result.status === 404) {
          toast.error(t('errors.resourceNotFound'));
          refreshBookings();
          return;
        }
        toast.error(t('booking.restoreFailed'));
        logger.error('Restore booking failed:', result.error);
        return;
      }
      toast.success(t('booking.restored', { id: booking.id }));
      refreshBookings();
    } catch (error) {
      toast.dismiss();
      toast.error(t('booking.restoreFailed'));
      logger.error('Restore booking failed:', error);
    }
  };

  const handleDelete = async (booking: Booking) => {
    try {
      const toastId = toast.loading(t('booking.deleting'));
      const result = await api.bookings.delete(booking.id);
      toast.dismiss(toastId);
      if (result.error) {
        if (result.status === 404) {
          toast.error(t('errors.resourceNotFound'));
          refreshBookings();
          closeDeleteModal();
          return;
        }
        toast.error(t('booking.deleteFailed'));
        logger.error('Delete booking failed:', result.error);
        return;
      }
      toast.success(t('booking.deleted', { id: booking.id }));
      refreshBookings();
      closeDeleteModal();
    } catch (error) {
      toast.dismiss();
      toast.error(t('booking.deleteFailed'));
      logger.error('Delete booking failed:', error);
    }
  };

  const handlePrintReceipt = async (booking: Booking) => {
    try {
      toast.loading(t('receipt.generating'));

      const printWindow = window.open('', '_blank', 'width=420,height=600');
      if (!printWindow) {
        toast.dismiss();
        toast.error(t('receipt.popupBlocked'));
        return;
      }

      const printedAt = new Date().toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland' });

      const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Receipt #${booking.id}</title>
    <style>
      body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; padding: 16px; }
      h1 { font-size: 18px; margin: 0 0 4px; }
      .muted { color: #6b7280; font-size: 12px; }
      .box { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-top: 12px; }
      .row { display: flex; justify-content: space-between; gap: 12px; margin: 8px 0; font-size: 13px; }
      .label { color: #6b7280; }
      .value { color: #111827; text-align: right; }
      .total { font-size: 16px; font-weight: 700; }
      hr { border: 0; border-top: 1px dashed #e5e7eb; margin: 12px 0; }
      @media print { .no-print { display: none; } body { padding: 0; } }
    </style>
  </head>
  <body>
    <h1>Nexcave Receipt</h1>
    <div class="muted">Printed at ${printedAt} (NZ)</div>

    <div class="box">
      <div class="row"><div class="label">Booking</div><div class="value">#${booking.id.toString().padStart(6, '0')}</div></div>
      <div class="row"><div class="label">Venue</div><div class="value">${booking.location_name}</div></div>
      <div class="row"><div class="label">Court</div><div class="value">${booking.court_name}</div></div>
      <div class="row"><div class="label">Date</div><div class="value">${booking.date}</div></div>
      <div class="row"><div class="label">Time</div><div class="value">${booking.start_time} - ${booking.end_time}</div></div>
      <hr />
      <div class="row"><div class="label">Customer</div><div class="value">${booking.customer_name || booking.customer_display_name || '—'}</div></div>
      <div class="row"><div class="label">Contact</div><div class="value">${booking.customer_phone || booking.customer_email || '—'}</div></div>
      <div class="row"><div class="label">Players</div><div class="value">${booking.player_count}</div></div>
      <hr />
      <div class="row total"><div class="label">Total</div><div class="value">${formatNZD(booking.total_price)}</div></div>
      <div class="row"><div class="label">Payment</div><div class="value">${booking.payment_method} / ${booking.payment_status}</div></div>
      <div class="row"><div class="label">Status</div><div class="value">${booking.status}</div></div>
    </div>

    <div class="no-print muted" style="margin-top: 12px;">Close this tab after printing.</div>
    <script>window.focus(); window.print();</script>
  </body>
</html>`;

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      toast.dismiss();
      toast.success(t('receipt.sentToPrinter'));

    } catch (error) {
      toast.dismiss();
      toast.error(t('receipt.printFailed'));
      logger.error('Print receipt failed:', error);
    }
  };

  const handleComplete = async (booking: Booking) => {
    try {
      const toastId = toast.loading(t('booking.completing'));

      const result = await api.bookings.complete(booking.id);

      toast.dismiss(toastId);
      if (result.error) {
        if (result.status === 404) {
          toast.error(t('errors.resourceNotFound'));
          refreshBookings();
          return;
        }
        toast.error(t('common.operationFailed'));
        logger.error('Complete booking failed:', result.error);
        return;
      }
      toast.success(t('booking.completed', { id: booking.id }));
      refreshBookings();

    } catch (error) {
      toast.dismiss();
      toast.error(t('common.operationFailed'));
      logger.error('Complete booking failed:', error);
    }
  };

  const openCancelModal = (booking: Booking) => {
    setCancelTarget(booking);
    setCancelReason('');
    setCancelError(null);
  };

  const closeCancelModal = () => {
    setCancelTarget(null);
    setCancelReason('');
    setCancelError(null);
  };

  const openDeleteModal = (booking: Booking) => {
    setDeleteTarget(booking);
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
  };

  const getStatusStats = () => {
    const stats = {
      all: bookings.length,
      confirmed: bookings.filter(b => b.status === 'confirmed').length,
      pending: bookings.filter(b => b.status === 'pending').length,
      completed: bookings.filter(b => b.status === 'completed').length,
      expired: bookings.filter(b => b.status === 'expired').length,
      cancelled: bookings.filter(b => b.status === 'cancelled').length,
    };

    // 检测冲突
    const conflicts = bookings.filter(b =>
      bookings.some(other =>
        other.id !== b.id &&
        other.court_name === b.court_name &&
        other.date === b.date &&
        other.start_time === b.start_time &&
        ['confirmed', 'pending'].includes(other.status) &&
        ['confirmed', 'pending'].includes(b.status)
      )
    );

    return { ...stats, conflicts: conflicts.length };
  };

  const stats = getStatusStats();

  return (
    <div className="space-y-6">
      {/* 页面标题和操作 */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('bookings.title')}</h1>
          <p className="text-gray-500 mt-1">{t('bookings.description')}</p>

          {/* 实时更新指示器 */}
          <div className="flex items-center gap-2 mt-2">
            <div className={`w-2 h-2 rounded-full ${realTimeUpdate ? 'bg-success-500 animate-pulse' : 'bg-gray-400'}`} />
            <span className="text-xs text-gray-500">
              {realTimeUpdate ? t('bookings.realTimeUpdate') : t('dashboard.pausedUpdate')}
            </span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <button
            onClick={() => realTimeUpdate ? setRealTimeUpdate(!realTimeUpdate) : refreshBookings()}
            className={`btn ${realTimeUpdate ? 'btn-secondary' : 'btn-outline'} gap-2 touch-manipulation`}
          >
            <RefreshCw className={`h-4 w-4 ${realTimeUpdate ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{realTimeUpdate ? t('bookings.realTimeUpdate') : t('bookings.manualRefresh')}</span>
            <span className="sm:hidden">{realTimeUpdate ? t('bookings.realTime') : t('bookings.refresh')}</span>
          </button>

          <button
            onClick={handleExport}
            className="btn btn-secondary gap-2 touch-manipulation"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">{t('common.export')}</span>
            <span className="sm:hidden">{t('common.export')}</span>
          </button>

          <Link to="/bookings/create" className="btn btn-primary gap-2 touch-manipulation">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">{t('bookings.createWalkIn')}</span>
            <span className="sm:hidden">{t('bookings.create')}</span>
          </Link>
        </div>
      </div>

      {/* 快速客户搜索 */}
      <div className="card card-comfortable">
        <div className="flex items-center gap-4 mb-4">
          <h3 className="font-semibold text-gray-900">{t('bookings.quickSearch')}</h3>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="w-full sm:w-56">
            <label className="block text-sm text-gray-600 mb-1">{t('bookings.date')}</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => {
                const newDate = e.target.value;
                setSelectedDate(newDate);
                setSearchParams((prev) => { prev.set('date', newDate); return prev; }, { replace: true });
              }}
              className="input w-full"
              disabled={allDates}
            />
            <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={allDates}
                onChange={(e) => setAllDates(e.target.checked)}
              />
              {t('bookings.allDates')}
            </label>
          </div>

          <div className="relative w-full sm:max-w-md">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>

            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('bookings.searchPlaceholder')}
              className="input pl-12 pr-10 w-full"
              autoComplete="off"
            />

            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {searchTerm && filteredBookings.length === 0 && !allDates && (
          <div className="mt-3 text-xs text-gray-500">
            <span>{t('bookings.noMatchForDate', { date: selectedDate })}</span>{' '}
            <button
              type="button"
              className="text-primary-700 underline"
              onClick={() => setAllDates(true)}
            >
              {t('bookings.searchAllDates')}
            </button>
          </div>
        )}
      </div>

      {/* 状态统计和冲突警告 */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <div
          className={clsx(
            "card cursor-pointer transition-all touch-manipulation hover:shadow-soft",
            selectedStatus === 'all' ? 'border-primary-300 bg-primary-50 ring-1 ring-primary-200' : 'hover:border-gray-300 hover:bg-gray-50'
          )}
          onClick={() => setSelectedStatus('all')}
        >
          <div className="p-3 sm:p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{stats.all}</div>
            <div className="text-sm text-gray-600">{t('common.all')}</div>
          </div>
        </div>

        <div
          className={clsx(
            "card cursor-pointer transition-all touch-manipulation hover:shadow-soft",
            selectedStatus === 'pending' ? 'border-warning-300 bg-warning-50 ring-1 ring-warning-200' : 'hover:border-gray-300 hover:bg-gray-50'
          )}
          onClick={() => setSelectedStatus('pending')}
        >
          <div className="p-3 sm:p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{stats.pending}</div>
            <div className="text-sm text-gray-600">{t('bookings.pending')}</div>
          </div>
        </div>

        <div
          className={clsx(
            "card cursor-pointer transition-all touch-manipulation hover:shadow-soft",
            selectedStatus === 'confirmed' ? 'border-success-300 bg-success-50 ring-1 ring-success-200' : 'hover:border-gray-300 hover:bg-gray-50'
          )}
          onClick={() => setSelectedStatus('confirmed')}
        >
          <div className="p-3 sm:p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{stats.confirmed}</div>
            <div className="text-sm text-gray-600">{t('bookings.confirmed')}</div>
          </div>
        </div>

        <div
          className={clsx(
            "card cursor-pointer transition-all touch-manipulation hover:shadow-soft",
            selectedStatus === 'completed' ? 'border-primary-300 bg-primary-50 ring-1 ring-primary-200' : 'hover:border-gray-300 hover:bg-gray-50'
          )}
          onClick={() => setSelectedStatus('completed')}
        >
          <div className="p-3 sm:p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{stats.completed}</div>
            <div className="text-sm text-gray-600">{t('bookings.completed')}</div>
          </div>
        </div>

        <div
          className={clsx(
            'card cursor-pointer transition-all touch-manipulation hover:shadow-soft',
            selectedStatus === 'expired'
              ? 'border-gray-400 bg-gray-100 ring-1 ring-gray-300'
              : 'hover:border-gray-300 hover:bg-gray-50'
          )}
          onClick={() => setSelectedStatus('expired')}
        >
          <div className="p-3 sm:p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{stats.expired}</div>
            <div className="text-sm text-gray-600">{t('bookings.expired')}</div>
          </div>
        </div>

        <div
          className={clsx(
            "card cursor-pointer transition-all touch-manipulation hover:shadow-soft",
            selectedStatus === 'cancelled' ? 'border-gray-400 bg-gray-100 ring-1 ring-gray-300' : 'hover:border-gray-300 hover:bg-gray-50'
          )}
          onClick={() => setSelectedStatus('cancelled')}
        >
          <div className="p-3 sm:p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{stats.cancelled}</div>
            <div className="text-sm text-gray-600">{t('bookings.cancelled')}</div>
          </div>
        </div>

        {/* 冲突警告 */}
        {stats.conflicts > 0 && (
          <div
            className="card cursor-pointer transition-all border-red-300 bg-red-50 animate-pulse"
            onClick={() => logger.info('View conflict details')}
          >
            <div className="p-4 text-center">
              <div className="text-2xl font-bold text-red-900">{stats.conflicts}</div>
              <div className="text-sm text-red-700">{t('bookings.timeConflict')}</div>
            </div>
          </div>
        )}
      </div>

      {/* 预订列表 */}
      <div className="card card-comfortable">
        <div className="space-y-4">
          {error && (
            <div className="p-4 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 flex items-center justify-between">
              <span>{error}</span>
              <button onClick={refreshBookings} className="text-red-700 underline">
                {t('common.refresh')}
              </button>
            </div>
          )}

          {loading && bookings.length === 0 && (
            <div className="text-center py-12">
              <RefreshCw className="h-8 w-8 text-gray-400 mx-auto mb-3 animate-spin" />
              <p className="text-gray-500">{t('common.loading')}</p>
            </div>
          )}

          <AnimatePresence>
            {filteredBookings.map((booking) => (
              <BookingCard
                key={booking.id}
                booking={booking}
                onQuickAction={handleQuickAction}
                showConflictWarning={stats.conflicts > 0}
                isNew={newBookingIds.has(booking.id)}
              />
            ))}
          </AnimatePresence>

          {filteredBookings.length === 0 && !loading && (
            <div className="text-center py-12">
              <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('bookings.noRecordsFound')}</h3>
              <p className="text-gray-500">{t('bookings.tryAdjustFilter')}</p>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={Boolean(cancelTarget)}
        onClose={closeCancelModal}
        title={t('bookings.cancelBooking')}
        size="md"
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            {cancelTarget ? `#${cancelTarget.id.toString().padStart(6, '0')} · ${cancelTarget.court_name}` : ''}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('bookings.cancellationReason')} <span className="text-red-500">*</span>
            </label>
            <textarea
              className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary ${cancelError ? 'border-red-500' : 'border-gray-300'
                }`}
              rows={3}
              value={cancelReason}
              onChange={(e) => {
                setCancelReason(e.target.value);
                if (cancelError) setCancelError(null);
              }}
              placeholder={t('booking.cancelReasonPlaceholder')}
            />
            {cancelError && (
              <p className="mt-2 text-sm text-red-600">{cancelError}</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn btn-secondary" onClick={closeCancelModal}>
              {t('common.cancel')}
            </button>
            <button
              className="btn btn-danger"
              onClick={() => cancelTarget && handleCancel(cancelTarget)}
            >
              {t('common.confirm')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(deleteTarget)}
        onClose={closeDeleteModal}
        title={t('bookings.deleteBooking')}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {t('booking.deleteConfirm')}
          </p>
          <div className="text-sm text-gray-600">
            {deleteTarget ? `#${deleteTarget.id.toString().padStart(6, '0')} · ${deleteTarget.court_name}` : ''}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn btn-secondary" onClick={closeDeleteModal}>
              {t('common.cancel')}
            </button>
            <button
              className="btn btn-danger"
              onClick={() => deleteTarget && handleDelete(deleteTarget)}
            >
              {t('common.delete')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BookingList;
